"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useAuth } from "@/contexts/AuthContext";
import {
  readOnboardingFlag,
  writeOnboardingFlag,
  ONBOARDING_STORAGE_KEYS,
  type OnboardingAudience,
  type OnboardingOpenSource,
} from "@/lib/onboarding";
import { trackClarityOnboardingTourOpen } from "@/lib/clarity";
import { trackMetaOnboardingTourOpen } from "@/lib/metaPixel";
import { resolveNextOnboardingPrompt } from "@/lib/onboardingEngine/engine";
import { ONBOARDING_PROMPT_REGISTRY } from "@/lib/onboardingEngine/registry";
import type {
  ActivePromptState,
  OnboardingPromptId,
  OnboardingProgressFlags,
} from "@/lib/onboardingEngine/types";
import { isDevToolboxEnabled } from "@/lib/devToolbox/isDevToolboxEnabled";

const HomepageTourModal = dynamic(
  () => import("@/components/onboarding/HomepageTourModal"),
  { ssr: false },
);

type OnboardingContextValue = {
  openTour: (options?: {
    audience?: OnboardingAudience;
    source?: OnboardingOpenSource;
  }) => void;
  closeTour: () => void;
  openPrompt: (promptId: OnboardingPromptId) => void;
  closePrompt: (promptId?: OnboardingPromptId) => void;
  completePrompt: (promptId: OnboardingPromptId) => void;
  isPromptOpen: (promptId: OnboardingPromptId) => boolean;
  activePromptId: OnboardingPromptId | null;
  isTourOpen: boolean;
  tourAudience: OnboardingAudience;
  tourSource: OnboardingOpenSource;
};

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

function HomepageTourHost({
  isOpen,
  onClose,
  audience,
  source,
}: {
  isOpen: boolean;
  onClose: () => void;
  audience: OnboardingAudience;
  source: OnboardingOpenSource;
}) {
  return (
    <HomepageTourModal
      isOpen={isOpen}
      onDismiss={onClose}
      audience={audience}
      source={source}
    />
  );
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const pathname = usePathname() ?? "";
  const [activePrompt, setActivePrompt] = useState<ActivePromptState | null>(
    null,
  );
  const pathnameRef = useRef(pathname);

  const readProgressFlags = useCallback((): OnboardingProgressFlags => {
    return {
      homepageAutoshowDone: readOnboardingFlag(
        ONBOARDING_STORAGE_KEYS.homepageAutoshowDone,
      ),
      guestHomepageAutoshowDone: readOnboardingFlag(
        ONBOARDING_STORAGE_KEYS.guestHomepageAutoshowDone,
      ),
      sellPreformDone: readOnboardingFlag(
        ONBOARDING_STORAGE_KEYS.sellPreformDone,
      ),
      marketIntroDone: readOnboardingFlag(
        ONBOARDING_STORAGE_KEYS.marketIntroDone,
      ),
      myItemsStatusModalDone: readOnboardingFlag(
        ONBOARDING_STORAGE_KEYS.myItemsStatusModalDone,
      ),
    };
  }, []);

  const flags = readProgressFlags();
  const isAuthenticated = Boolean(user);
  const suppressAutoPrompts = pathname.startsWith("/admin");

  const writeCompletionFlag = useCallback(
    (promptId: OnboardingPromptId, audience: OnboardingAudience) => {
      let key: string | null = null;
      if (promptId === "homepageTour") {
        key =
          audience === "auth"
            ? ONBOARDING_STORAGE_KEYS.homepageAutoshowDone
            : ONBOARDING_STORAGE_KEYS.guestHomepageAutoshowDone;
      } else if (promptId === "sellIntro") {
        key = ONBOARDING_STORAGE_KEYS.sellPreformDone;
      } else if (promptId === "marketIntro") {
        key = ONBOARDING_STORAGE_KEYS.marketIntroDone;
      } else if (promptId === "myItemsStatusIntro") {
        key = ONBOARDING_STORAGE_KEYS.myItemsStatusModalDone;
      }

      if (!key) return;
      writeOnboardingFlag(key, true);
    },
    [],
  );

  const activatePrompt = useCallback(
    (
      promptId: OnboardingPromptId,
      options?: {
        audience?: OnboardingAudience;
        source?: OnboardingOpenSource;
        openedManually?: boolean;
      },
    ) => {
      if (promptId === "homepageTour") {
        const audience: OnboardingAudience =
          options?.audience ?? (user ? "auth" : "guest");
        const source = options?.source ?? "floating_help";
        const openedManually =
          options?.openedManually ?? source !== "homepage_autoshow";
        setActivePrompt({ id: promptId, audience, source, openedManually });
        // Keep analytics stable for tour opens regardless of entry path.
        trackClarityOnboardingTourOpen({ audience, source });
        trackMetaOnboardingTourOpen({ audience, source });
        return;
      }
      setActivePrompt({
        id: promptId,
        openedManually: options?.openedManually ?? false,
      });
    },
    [user],
  );

  const closePrompt = useCallback((promptId?: OnboardingPromptId) => {
    setActivePrompt((prev) => {
      if (!prev) return prev;
      if (promptId && prev.id !== promptId) return prev;
      return null;
    });
  }, []);

  const completePrompt = useCallback(
    (promptId: OnboardingPromptId) => {
      const audience: OnboardingAudience =
        activePrompt?.id === "homepageTour" && activePrompt.audience
          ? activePrompt.audience
          : user
            ? "auth"
            : "guest";
      writeCompletionFlag(promptId, audience);
      setActivePrompt((prev) => (prev?.id === promptId ? null : prev));
    },
    [activePrompt, user, writeCompletionFlag],
  );

  const openPrompt = useCallback(
    (promptId: OnboardingPromptId) => {
      activatePrompt(promptId, { openedManually: true });
    },
    [activatePrompt],
  );

  const openTour = useCallback(
    (options?: {
      audience?: OnboardingAudience;
      source?: OnboardingOpenSource;
    }) => {
      activatePrompt("homepageTour", {
        audience: options?.audience,
        source: options?.source ?? "floating_help",
        openedManually: true,
      });
    },
    [activatePrompt],
  );

  const closeTour = useCallback(() => {
    closePrompt("homepageTour");
  }, [closePrompt]);

  const isPromptOpen = useCallback(
    (promptId: OnboardingPromptId) => activePrompt?.id === promptId,
    [activePrompt],
  );

  const onboardingEngineContext = useMemo(
    () => ({
      pathname,
      isAuthLoading: isLoading,
      isAuthenticated,
      flags,
      suppressAutoPrompts,
    }),
    [pathname, isLoading, isAuthenticated, flags, suppressAutoPrompts],
  );

  // Resolve one auto-prompt at a time from the central registry.
  useEffect(() => {
    if (activePrompt) return;
    const nextPromptId = resolveNextOnboardingPrompt(
      onboardingEngineContext,
      ONBOARDING_PROMPT_REGISTRY,
    );
    if (!nextPromptId) return;
    if (nextPromptId === "homepageTour") {
      const timer = setTimeout(() => {
        activatePrompt("homepageTour", {
          audience: user ? "auth" : "guest",
          source: "homepage_autoshow",
          openedManually: false,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      activatePrompt(nextPromptId, { openedManually: false });
    }, 0);
    return () => clearTimeout(timer);
  }, [activePrompt, onboardingEngineContext, activatePrompt, user]);

  // If homepage auto-tour is left by route change, mark seen and close.
  useEffect(() => {
    const prev = pathnameRef.current;
    if (prev === pathname) return;
    pathnameRef.current = pathname;
    if (
      activePrompt?.id === "homepageTour" &&
      activePrompt.source === "homepage_autoshow" &&
      prev === "/" &&
      pathname !== "/" &&
      pathname !== ""
    ) {
      const audience = activePrompt.audience ?? (user ? "auth" : "guest");
      writeCompletionFlag("homepageTour", audience);
      closePrompt("homepageTour");
    }
  }, [pathname, activePrompt, user, writeCompletionFlag, closePrompt]);

  const isTourOpen = activePrompt?.id === "homepageTour";
  const tourAudience = activePrompt?.audience ?? "guest";
  const tourSource = activePrompt?.source ?? "floating_help";

  useEffect(() => {
    if (!isDevToolboxEnabled()) return undefined;
    let unsubscribe = () => {};
    void import("@/lib/devToolbox/adapters/onboarding").then(
      ({ subscribeOnboardingDevToolboxAdapter }) => {
        unsubscribe = subscribeOnboardingDevToolboxAdapter({
          openTour,
          openPrompt,
        });
      },
    );
    return () => unsubscribe();
  }, [openTour, openPrompt]);

  const value = useMemo(
    () => ({
      openTour,
      closeTour,
      openPrompt,
      closePrompt,
      completePrompt,
      isPromptOpen,
      activePromptId: activePrompt?.id ?? null,
      isTourOpen,
      tourAudience,
      tourSource,
    }),
    [
      openTour,
      closeTour,
      openPrompt,
      closePrompt,
      completePrompt,
      isPromptOpen,
      activePrompt,
      isTourOpen,
      tourAudience,
      tourSource,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <HomepageTourHost
        isOpen={isTourOpen}
        onClose={() => completePrompt("homepageTour")}
        audience={tourAudience}
        source={tourSource}
      />
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}
